import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { format } from "date-fns";
import { orderBy } from "lodash";

import Select from "react-select";
import Tooltip from "@/tooltip";
import PensionPlan from "./PensionPlan";

import { ArrowsRightLeftIcon, ReceiptPercentIcon } from "@heroicons/react/20/solid";
import { Actions as EMPLOYEE_ACTIONS } from "@/redux/actions/employeeActions";
import { NotificationManager } from "react-notifications";
import cleanDeep from "clean-deep";
import { mapPensionsForDropdown, mapSalariesForDropdown } from "@/common/helpers/Remappers";
import { useLocation } from "react-router-dom";

const AutoEnrolment = ({ updatePostponeDateModal, disabled, forNewEmployee, pensionRules, forReview }) => {
	const classes = /*pensionRules?.enrolmentBracketCode &&*/ !forNewEmployee
		? "col-lg-4 col-sm-12 col-xs-12 left"
		: "col-xs-12";

	return (
		<div className={`${classes} form-section`}>
			<div
				className="col-xs-8 section-header"
				style={{ paddingBottom: 0, marginBottom: "0.5rem" }}
			>
				<h1 className="block-title">
					<ReceiptPercentIcon
						style={{
							padding: "0.5rem",
							height: "3rem",
							borderRadius: "5rem",
							marginRight: "1rem",
							color: "white",
							background: "#ea611c",
							verticalAlign: "middle",
						}}
					/>
					Auto Enrolment
				</h1>
			</div>
			<div className="col-xs-4">
				{!forNewEmployee && !forReview && pensionRules ? (
					pensionRules?.pensionStatus == "Not Assessed" ? (
						<button
							disabled={disabled}
							className="btn btn-xs btn-default pull-right"
							style={{ marginTop: "1.5rem" }}
							onClick={() => updatePostponeDateModal()}
						>
							<ArrowsRightLeftIcon
								className="h-5 w-5"
								style={{ marginTop: "3px", float: "left" }}
							/>
							<div style={{ float: "left" }}>Postpone</div>
						</button>
					) : (
						<div
							className="btn btn-xs btn-default pull-right"
							style={{ marginTop: "1.5rem" }}
						>
							<div style={{ float: "left" }}>
								{pensionRules?.pensionStatus} <br />
								<small>
									{pensionRules?.pensionStatus === "Postponed" ? " until " : " on "}{" "}
									{format(new Date(pensionRules?.pensionStatusDate || null), "do MMM yyyy")}
								</small>
							</div>
						</div>
					)
				) : null}
			</div>
			<div className="clearfix" />
			<div
				className="col-xs-12 form-container"
				style={{ minHeight: "13.5rem" }}
			>
				{pensionRules?.message ? (
					<div className="form-container">
						<span>{pensionRules?.message}</span>
						<br></br>
						{pensionRules?.enrolmentBracketCode ? (
							<div className="badge badge-primary">
								{pensionRules?.enrollByDefault ? "Must be Enrolled" : "Can Be Enrolled"}
							</div>
						) : null}
					</div>
				) : null}
				{pensionRules?.enrolmentBracketCode ? (
					<>
						<div className="col-xs-4 pl-0">
							<div className="form-material">
								<label htmlFor="material-text">
									<Tooltip
										content={
											<div style={{ whiteSpace: "nowrap" }}>
												<p style={{ color: "white", fontWeight: 700 }}>
													Pension Bracket Explained
												</p>
												<ul style={{ whiteSpace: "nowrap", textAlign: "left" }}>
													<li style={{ marginBottom: "1rem" }}>
														<strong>Age</strong>-<strong>Salary</strong>-
														<strong>Right</strong>
														<br />
														{pensionRules?.enrolmentBracketCode}
													</li>
													<li>
														Age is {">="}&nbsp;
														{pensionRules?.enrolmentBracketCode?.split("-")?.[0]}
													</li>
													<li>
														Weekly salary {">="}&nbsp;
														{pensionRules?.enrolmentBracketCode?.split("-")?.[1]}
													</li>
													<li>
														Employee is entitled to:&nbsp;
														{pensionRules?.enrolmentBracketCode?.split("-")?.[2]}
													</li>
												</ul>
											</div>
										}
									>
										Bracket
									</Tooltip>
								</label>
								<input
									className="form-control form-input"
									value={pensionRules?.enrolmentBracketCode}
									disabled
								/>
							</div>
						</div>
						<div className="col-xs-4 pl-0">
							<div
								className="form-material"
								style={{ position: "relative" }}
							>
								<label htmlFor="material-text">Minimum</label>
								<div style={{ position: "absolute", top: 8, right: 0 }}>%</div>
								<input
									style={{ paddingRight: "1rem" }}
									className="form-control form-input"
									value={pensionRules?.minimumContribution}
									disabled
								/>
							</div>
						</div>
						<div className="col-xs-4 pl-0">
							<div
								className="form-material"
								style={{ position: "relative" }}
							>
								<label htmlFor="material-text">Employer</label>
								<div style={{ position: "absolute", top: 8, right: 0 }}>%</div>
								<input
									style={{ paddingRight: "1rem" }}
									className="form-control form-input"
									id="ageBracket"
									name="ageBracket"
									value={pensionRules?.employerContribution}
									disabled
								/>
							</div>
						</div>
					</>
				) : null}
			</div>
		</div>
	);
};

const PensionDetails = ({
	disabled,
	pensions,
	forNewEmployee,
	prevPage,
	nextPage,
	deferredUntil,
	updatePostponeDateModal,
	currency,
	getPensionEnrolment,
	pensionRules,
	paySchedules,
	agreement,
	forReview,
	salary,
	groups,
	confirmChange,
	confirmModal,
	employeeStartDate,
	deletePensionModal,
	forReviewAgreement,
}) => {
	const [optIn, setOptIn] = useState(false);
	const [optOut, setOptOut] = useState(false);
	const [reEnroll, setReEnroll] = useState(false);
	const [selectedPension, setSelectedPension] = useState(pensions?.[pensions?.length - 1]?.value);
	const isLast = pensions?.[pensions?.length - 1]?.value === selectedPension;

	const [data, setData] = useState({});
	const updateField = (field, value) => {
		const newData = {
			...data,
			[field]: value,
		};
		setData(newData);
	};

	const percentageOptions = useMemo(() => {
		return [
			{ label: "%", value: true },
			{ label: currency, value: false },
		];
	}, [currency]);

	useEffect(() => {
		setSelectedPension(pensions?.[pensions?.length - 1]?.value);
		setOptIn(false);
		setOptOut(false);
		setReEnroll(false);
	}, [JSON.stringify(pensions)]);

	const pension = useMemo(() => {
		if (reEnroll) return {};
		const selected = pensions?.find(
			(p) => p.value == selectedPension || (forReview ? forReview?.id === p.value : false),
		);
		if (selected) return selected?.data;
		const last = pensions?.[pensions?.length - 1];
		return last?.data;
	}, [selectedPension, JSON.stringify(pensions), reEnroll]);

	const pensionGroups = useMemo(() => {
		const { start: pensionStart, end: pensionEnd } = pension || {};
		const { newDate } = forReview || {};
		const referenceStart = newDate ? newDate : pensionStart;
		const availableGroups = groups
			?.map((group) => {
				const salaryPaySchedule = salary?.paySchedule || paySchedules?.[0]?.value;
				const { endDate, startDate, name, id, paySchedule: pensionPaySchedule } = group;
				const hasGroupStarted = referenceStart ? referenceStart >= startDate : true;
				const hasGroupEnded = pensionEnd ? endDate > pensionEnd : false;

				// only show groups that are available for the period the employee is employed
				if (!hasGroupStarted || hasGroupEnded) return;

				const matchingPaySchedule = pensionPaySchedule ? pensionPaySchedule === salaryPaySchedule : true;
				const paySchedule = paySchedules?.find(({ value }) => value === pensionPaySchedule)?.label;
				const start = startDate ? format(new Date(startDate), "dd MMM yyyy") : null;
				const end = endDate && endDate != "null" ? format(new Date(endDate), "dd MMM yyyy") : "now";
				const statusMessage = `Group available ${start ? `from ${start} ` : ""}${end ? `until ${end}` : ""}`;

				return {
					label: name,
					value: id,
					statusMessage,
					isDisabled: !matchingPaySchedule,
					description: matchingPaySchedule ? "" : `This pension group is only available for ${paySchedule}`,
				};
			})
			?.filter(Boolean);

		return availableGroups || [];
	}, [pension?.value, JSON.stringify(groups)]);

	useEffect(() => {
		// if (forNewEmployee) return;
		setData({
			id: pension?.id,
			occupation: pension?.occupation,
			pensionGroup: pension?.pensionGroup,
			customPensionContribution: pension?.customPensionContribution,
			employeeContribution: pension?.employeeContribution,
			employerContribution: pension?.employerContribution,
			employeePercentage: pension?.employeePercentage,
			employerPercentage: pension?.employerPercentage,
			optInDate: pension?.optInDate,
			optOutDate: pension?.optOutDate,
			enrolledDate: pension?.enrolledDate,
			joinDate: pension?.joinDate,
			enrolmentBracketCode: pension?.enrolmentBracketCode,
		});
	}, [pension?.id]);

	const contributions = useMemo(() => {
		const { employeeContribution, employerContribution, employeePercentage, employerPercentage } = (() => {
			if (data?.customPensionContribution) return data;
			if (data?.pensionGroup) return groups?.find((item) => item.id == data.pensionGroup) || {};
			return {};
		})();

		return {
			employeeContribution:
				typeof employeeContribution !== "undefined" ? parseFloat(employeeContribution) || 0 : "",
			employerContribution:
				typeof employerContribution !== "undefined" ? parseFloat(employerContribution) || 0 : "",
			employeePercentage: typeof employeePercentage !== "undefined" ? employeePercentage : true,
			employerPercentage: typeof employerPercentage !== "undefined" ? employerPercentage : true,
		};
	}, [pension?.id, data?.customPensionContribution, data?.pensionGroup, JSON.stringify(groups), data?.employeeContribution, data?.employerContribution]);

	useEffect(() => {
		const { weeklySalary, start, employmentType } = salary || {};
		getPensionEnrolment(weeklySalary, forReview?.newDate || start, agreement, employmentType);
	}, [salary?.value, deferredUntil, salary?.employentType]);

	const submitAssessment = () => {

		const dataToSubmit = {
			assessmentDate: salary?.start,
			enrolmentBracketCode: pensionRules?.enrolmentBracketCode,
		};

		try {
			confirmModal({
				input: cleanDeep(dataToSubmit, {
					emptyStrings: false,
					emptyArrays: false,
					emptyObjects: false,
				}),
				status: !dataToSubmit.optOutDate && !forNewEmployee && !forReview,
				formType: "Pension",
				previousDate: pension?.end != "null" ? pension?.end : pension?.start,
				fixedDate: optIn && data.optInDate,
				cannotReplace: reEnroll || !pension,
				isLast,
			});

			if (forNewEmployee) {
				confirmChange(employeeStartDate, pensions?.length > 0, undefined, nextPage);
			}
		} catch (error) {
			console.error(error);
		}
	}

	const onSubmit = () => {
		const { employeeContribution, employerContribution, employeePercentage, employerPercentage } = contributions;

		if (deferredUntil) {
			nextPage?.();
			return;
		}

		if ((optIn || pension?.optInDate) && !data?.optInDate) {
			NotificationManager.error("Please select an opt in date", "Saving Pension Plan");
			return;
		}
		if (!data.pensionGroup) {
			NotificationManager.error("Please select a pension group", "Saving Pension Plan");
			return;
		}

		if (employeePercentage && employerPercentage) {
			if (!pensionRules?.enrollByDefault && !data.pensionGroup) return;
			const totalContribution = employeeContribution + employerContribution;
			if (!data.pensionGroup) {
				NotificationManager.error("Employee must be enrolled in a pension group", "Saving Pension Plan");
				return;
			}
			if (employerContribution < pensionRules?.employerContribution) {
				NotificationManager.error(
					`Employer Contributions cannot be less than ${pensionRules?.employerContribution}%`,
					"Saving Pension Plan",
				);
				return;
			}
			if (totalContribution < pensionRules?.minimumContribution) {
				NotificationManager.error(
					`Total Contributions cannot be less than ${pensionRules?.minimumContribution}%`,
					"Saving Pension Plan",
				);
				return;
			}
		}

		const dataToSubmit = {
			...data,
			...contributions,
			newDate: forReview?.newDate,
			enrolmentBracketCode: pensionRules?.enrolmentBracketCode,
		};

		try {
			confirmModal({
				input: cleanDeep(dataToSubmit, {
					emptyStrings: false,
					emptyArrays: false,
					emptyObjects: false,
				}),
				status: !dataToSubmit.optOutDate && !forNewEmployee && !forReview,
				formType: "Pension",
				previousDate: pension?.end != "null" ? pension?.end : pension?.start,
				fixedDate: optIn && data.optInDate,
				cannotReplace: reEnroll || !pension,
				isLast,
			});

			if (dataToSubmit.optOutDate) {
				confirmChange(dataToSubmit.start, true);
			}

			if (forReview) {
				const newDate = data?.start != forReview.newDate;
				confirmChange(newDate ? data?.start : forReview.newDate, !newDate, forReviewAgreement);
			}

			if (forNewEmployee) {
				confirmChange(employeeStartDate, pensions?.length > 0, undefined, nextPage);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const ButtonManager = () => {
		const buttonMessage = (() => {
			if (reEnroll) return "Re-Enroll at later date";
			if (data.id) return "Update Pension Details";
			if (!data?.id) return "Enrol to Scheme";
		})();

		if (forNewEmployee) {
			return (
				<div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
					{prevPage && (
						<button
							className="btn btn-default"
							type="button"
							onClick={prevPage}
						>
							Back
						</button>
					)}
					<button
						className="btn btn-success right"
						type="submit"
						style={{ marginLeft: "1rem" }}
						onClick={onSubmit}
					>
						Save &amp; Continue
					</button>
					{nextPage && (pensionRules?.OPT || !pensionRules?.enrolmentBracketCode) && (
						<button
							className="btn btn-default pull-right"
							type="button"
							onClick={submitAssessment}
						>
							Confirm Non-Eligibility
						</button>
					)}
				</div>
			);
		} else {
			return (
				<button
					className="btn btn-success pull-right"
					type="submit"
					style={{ marginBottom: "30px" }}
					onClick={onSubmit}
				>
					{buttonMessage}
				</button>
			);
		}
		return null;
	};

	const componentOrder = orderBy(
		[
			{ component: PensionPlan, order: 0 },
			{ component: AutoEnrolment, order: 1 },
		],
		"order",
		forNewEmployee ? "desc" : "asc",
	);

	return (
		<div className="px-8">
			{forReview ? (
				<div
					className="col-xs-12 form-section"
					style={{ marginTop: "3rem" }}
				>
					<div className="col-xs-2 section-header">
						<h1 className="block-title">Salary</h1>
					</div>
					<div className="col-xs-10">
						<Select
							value={salary}
							isDisabled={forReview}
						/>
					</div>
				</div>
			) : null}

			{!forNewEmployee && !forReview ? (
				<div
					className="col-xs-12 form-section"
					style={{ marginTop: "3rem" }}
				>
					<div className="col-xs-2 section-header">
						<h1 className="block-title">Pension</h1>
					</div>
					<div className="col-xs-10">
						<Select
							options={pensions}
							value={pensions?.find((p) => p.value === selectedPension)}
							isDisabled={!pensions?.length}
							placeholder="No Pension Plans"
							onChange={(pension) => {
								setSelectedPension(pension.value);
							}}
						/>
						{!disabled && isLast && pensions?.length > 1 && (
							<>
								<button
									className="btn btn-danger pull-right"
									type="submit"
									style={{
										position: "absolute",
										top: 6,
										right: 50,
										fontSize: 12,
										fontWeight: "normal",
										padding: "4px 8px",
									}}
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										deletePensionModal(pension?.id);
									}}
								>
									Delete
								</button>
							</>
						)}
					</div>
				</div>
			) : null}

			{componentOrder?.map((page) => {
				return (
					<page.component
						{...{
							data,
							isLast,
							pensionGroups,
							pensionRules,
							salary,
							pension,
							lastPension: pensions?.[pensions?.length - 1]?.data,
							updateField: (field, value) => updateField(field, value),
							disabled,
							reEnroll,
							setReEnroll: (value) => setReEnroll(value),
							setData: (data) => setData(data),
							contributions,
							optIn,
							setOptIn: (value) => setOptIn(value),
							updatePostponeDateModal,
							optOut,
							setOptOut: (value) => setOptOut(value),
							percentageOptions,
							forNewEmployee,
						}}
					/>
				);
			})}

			<div className="clearfix" />
			<ButtonManager />
			<div className="clearfix"></div>
		</div>
	);
};

const mapStateToProps = (state, { occupation, forReview }) => {
	const { salaries: employeeSalaries, agreementInfo, pensionPlans: employeePensions } = state.employee || {};
	const { currency } = state.company?.settings || {};
	const pensionPlans = employeePensions?.filter((p) => (occupation ? p.occupation === occupation : true));

	const isTerminated = agreementInfo?.terminationDate
		? agreementInfo?.terminationDate < format(new Date(), "yyyy-MM-dd")
		: false;

	const salaries = mapSalariesForDropdown(employeeSalaries, currency);
	const pensions = mapPensionsForDropdown(pensionPlans, state?.pensions?.group?.list);

	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		salary: salaries?.find((s) => s.value === forReview?.salaryId) || salaries?.[0],
		pensions,
		deferredUntil: agreementInfo?.enrolmentDeferredUntil,
		agreement: agreementInfo?.id,
		employeeName: agreementInfo?.employeeDetails
			? `${agreementInfo?.employeeDetails?.givenName} ${agreementInfo?.employeeDetails?.familyName}`
			: "This employee",
		groups: state?.pensions?.group?.list,
		isTerminated,
		employeeStartDate: agreementInfo?.engagementDate,
		disabled: state?.permissions?.my?.permissions?.people === "Read",
		pensionRules: state.pensions?.pensionEnrolment?.[agreementInfo?.id],
		currency: state.company?.settings?.currency,
		paySchedules: state.company?.settings?.paySchedules?.map(({ id, name }) => ({ label: name, value: id })) || [],
	};
};

const mapDispatchToProps = (dispatch, { notification }) => ({
	getPensionEnrolment: (weeklySalary, dated, agreement, employmentType) =>
		dispatch(EMPLOYEE_ACTIONS.getPensionEnrolment({ weeklySalary, dated, employmentType }, { agreement })),
	saveAgreementPension: (data, agreement, suppressNotification) =>
		dispatch(EMPLOYEE_ACTIONS.saveAgreementPension(data, { agreement, notification, suppressNotification })),
	putAgreementPension: (data, agreement, suppressNotification) =>
		dispatch(EMPLOYEE_ACTIONS.putAgreementPension(data, { agreement, notification, suppressNotification })),
	updatePostponeDateModal: () => dispatch(EMPLOYEE_ACTIONS.updatePostponeDateModal(true)),
	confirmModal: (options) => dispatch(EMPLOYEE_ACTIONS.confirmModal(options)),
	deletePensionModal: (id) => dispatch(EMPLOYEE_ACTIONS.deletePensionModal(id)),
	confirmChange: (date, replace, agreement, onSuccess) =>
		dispatch(EMPLOYEE_ACTIONS.confirmChange({ date, replace, agreement, notification, onSuccess })),
});

export default connect(mapStateToProps, mapDispatchToProps)(PensionDetails);
